import { useState } from 'react';
import { IPriceRule } from '../../types/price.types';
import { IPriceBandWithVenues } from '../../types/priceBand.types';
import { TPriceBandAndRules } from '../PriceSchemeBandsRulesTab/types';
import { useCreatePriceBand } from '../../hooks/useCreatePriceBand';
import { useDeletePriceBand } from '../../hooks/useDeletePriceBand';
import { useUpdatePriceBand } from '../../hooks/useUpdatePriceBand';
import { PriceBandRuleSteps } from '../PriceBandRuleModal';
import { TTableRow, isInlineCreateRow } from './types';

type TEditingState = {
  rule: IPriceRule | null;
  step: number | null;
};

const DEFAULT_EDITING_STATE: TEditingState = { rule: null, step: null };

export const usePriceBandActions = (priceSchemeId: string, priceBands: IPriceBandWithVenues[]) => {
  const [isCreatingInline, setIsCreatingInline] = useState(false);
  const [editingPriceBandId, setEditingPriceBandId] = useState<string | null>(null);
  const [venueModalPriceBand, setVenueModalPriceBand] = useState<TPriceBandAndRules | null>(null);
  const [deletingPriceBand, setDeletingPriceBand] = useState<IPriceBandWithVenues | null>(null);
  const [editingRule, setEditingRule] = useState<TEditingState>(DEFAULT_EDITING_STATE);

  const { mutateAsync: createPriceBand } = useCreatePriceBand(() => {}, priceSchemeId);
  const { mutate: deletePriceBand, isLoading: isDeleting } = useDeletePriceBand(() => setDeletingPriceBand(null));
  const { mutateAsync: updatePriceBand } = useUpdatePriceBand(() => {});

  const handleStartCreate = () => setIsCreatingInline(true);
  const handleEditName = (priceBandId: string) => setEditingPriceBandId(priceBandId);

  const handleClose = () => {
    setIsCreatingInline(false);
    setEditingPriceBandId(null);
  };

  const handleSubmit = async (name: string, row: TTableRow) => {
    if (isInlineCreateRow(row)) {
      await createPriceBand({ name });
    } else if (!('_type' in row) && row.price_band?.id) {
      await updatePriceBand({ id: row.price_band.id, priceBandId: priceSchemeId, name });
    }
  };

  const handleEditVenues = (priceBand: TPriceBandAndRules) => setVenueModalPriceBand(priceBand);
  const handlePriceBandDelete = (priceBand: IPriceBandWithVenues) => setDeletingPriceBand(priceBand);
  const handleConfirmDelete = () => {
    if (deletingPriceBand) {
      deletePriceBand({ priceBand: deletingPriceBand, priceSchemeId });
    }
  };

  const resetEditingRule = () => setEditingRule(DEFAULT_EDITING_STATE);
  const handleEditClick = (rule: IPriceRule, step: PriceBandRuleSteps) => setEditingRule({ rule, step });

  const { rule } = editingRule;
  const priceBandToEdit = rule
    ? {
        priceBandId: rule.price_band?.id || '',
        name: rule.price_band?.name || '',
        venues: priceBands.find((pb) => pb.priceBandId === rule.price_band?.id)?.venues || [],
        ...(!rule.price_rule_id.includes('_rule') && {
          price_rule_id: rule.price_rule_id,
          adjustment: rule.adjustment,
          rounding: rule.rounding,
        }),
      }
    : null;

  return {
    isCreatingInline,
    editingPriceBandId,
    venueModalPriceBand,
    setVenueModalPriceBand,
    deletingPriceBand,
    setDeletingPriceBand,
    editingRule,
    priceBandToEdit,
    isDeleting,
    handleStartCreate,
    handleEditName,
    handleClose,
    handleSubmit,
    handleEditVenues,
    handlePriceBandDelete,
    handleConfirmDelete,
    resetEditingRule,
    handleEditClick,
  };
};
